import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { projectStore } from '@/storage/projectStorePort';
import { assembleApp } from '../api/build';
import { publishToSaddle } from '../api/hosting';
import { trackPixel } from '../analytics/metaPixel';
import { useProjects } from './projects';
import { schemasForPublish } from './publishSchemas';

/**
 * A web app's live page, and whether it has what the owner has.
 *
 * Two things change a live page from this client: "Publish" in PublishPanel, and a chat edit,
 * which publishes its own change the moment it is saved — as the phone always has. Both go
 * through `publishLatest`, so there is ONE content-update path, and both read the project as it
 * is STORED rather than the copy a screen was rendered with: a chat edit waits a long time on
 * the server, and a publish that laid its snapshot back down afterwards would put the pre-edit
 * source on the device (and, on the next publish, the page). The phone learned this in
 * src/store/webEditPublish.ts; this is the same rule, written against the browser's store.
 */

/** What an owner is told when a change reached a page that is switched off. */
export const SAVED_BUT_OFFLINE =
  "Saved. Your page is offline, so visitors won't see this until it is put back online.";

/** What an owner is told when a change is on the page people open. */
export const LIVE_UPDATED = 'Done — your live page is updated.';

/**
 * The source the live page was last published from, as a fingerprint.
 *
 * A fingerprint rather than a "needs publishing" flag, because a flag is only as good as every
 * writer remembering to set it, and a fingerprint cannot be forgotten: anything that changes the
 * source makes it stale by itself. It is written ONLY after the server confirms a publish, so a
 * publish that failed — or a tab closed halfway through one — leaves the page reading as behind,
 * which is true.
 */
type WithLiveSource = AppDefinition & { publishedSourceHash?: string };

/** FNV-1a over the UTF-16 code units, with the length: cheap, stable, and never sent anywhere. */
export function sourceHash(source: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    h ^= source.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${source.length}:${(h >>> 0).toString(16)}`;
}

/** Record that the live page now carries `source`. */
export function markPublished<T extends AppDefinition>(definition: T, source: string): T {
  return { ...definition, publishedSourceHash: sourceHash(source) };
}

/**
 * - `unpublished`: no page on record (never published, or made private).
 * - `current`: the page was last published from exactly this source.
 * - `behind`: the source has changed since — an edit whose publish failed, or has not finished.
 * - `unknown`: published before this record kept a fingerprint.
 *
 * `unknown` is not `current`. Until this, a web edit changed the app here and left the page
 * alone until somebody pressed "Publish changes", so a record from then may well be behind —
 * and answering "current" would hide the one button that puts it right.
 */
export type LiveState = 'unpublished' | 'current' | 'behind' | 'unknown';

export function liveState(definition: AppDefinition): LiveState {
  if (!definition.webUrl) return 'unpublished';
  const at = (definition as WithLiveSource).publishedSourceHash;
  if (at === undefined) return 'unknown';
  return at === sourceHash(definition.source ?? '') ? 'current' : 'behind';
}

/** Said when an edit's source was made from a version that has since been replaced. */
export const CHANGED_MEANWHILE =
  'Your app was changed while this edit was being made, so nothing was overwritten. Send your request again to apply it to the current version.';

/** The project as it is stored now, by ITS id — the person may have opened another app since. */
async function stored(projectId: string, ownerId: string): Promise<Project> {
  const project = await projectStore().getById(projectId, ownerId);
  if (!project) throw new Error('This app could not be found in this browser, so nothing was changed.');
  return project;
}

/**
 * The source as it is stored now: what an edit must be made FROM.
 *
 * Not the source the screen was drawn with. That copy is read when the project is opened and
 * not again, so once something else changed the stored app — another tab of this browser, say —
 * an edit made from the screen's copy was refused by `saveSource` AFTER the paid /edit call, and
 * "send your request again" made the same stale edit and paid again, until the page was reloaded.
 */
export async function storedSource(projectId: string, ownerId: string): Promise<string> {
  return (await stored(projectId, ownerId)).definition.source ?? '';
}

/**
 * Write an edit's new source onto the stored record, and nothing else.
 *
 * `sent` is the source the edit was made FROM. If the stored source has moved on, there is no
 * safe merge — laying the model's text over it would silently undo the other change — so the
 * save refuses and says so. Everything else (the address and catalogue id a publish wrote while
 * the edit was on its way, above all) is kept: spreading the screen's old copy instead lost the
 * catalogue id, and the next publish registered the app a second time at `<slug>-2`.
 */
export async function saveSource(projectId: string, ownerId: string, sent: string, next: string): Promise<void> {
  const { definition } = await stored(projectId, ownerId);
  if ((definition.source ?? '') !== sent) throw new Error(CHANGED_MEANWHILE);
  await useProjects.getState().updateProject(projectId, { definition: { ...definition, source: next } }, ownerId);
}

export interface LivePublish {
  /** The catalogue id the app is published under. */
  appId: string;
  siteUrl: string;
  /** False when the page is switched off: a content update keeps an offline page offline. */
  webPublished: boolean;
}

/**
 * Assemble the stored record and put it on its page, then write back where it went.
 *
 * `putOnline` is for an explicit decision to put the page UP ("Publish", "Publish it again").
 * Without it this is a CONTENT update: the server replaces the document and keeps a page its
 * owner took offline offline and a password page behind its password — which is the only kind
 * of publish a chat edit may make.
 *
 * Assembled under the app's OWN id and hosted under the catalogue's. They are different ids and
 * swapping them breaks something in each direction: the first is the localStorage namespace
 * baked into the page, so a different one strands every visitor's saved data; the second is the
 * only one `/apps/{appId}/…` resolves, so hosting under the first is a 404.
 */
export async function publishLatest(
  projectId: string,
  ownerId: string,
  opts: { putOnline?: boolean } = {},
): Promise<LivePublish> {
  const project = await stored(projectId, ownerId);
  const { definition } = project;
  const source = definition.source ?? '';
  /*
   * Whether this app has EVER been online, from the record as stored: the one fact AppPublished
   * turns on. Decided here, where every publish from a project passes, because the first one is
   * no longer always "Publish" — a build whose own publish failed goes online on its first chat
   * edit, and a count kept by PublishPanel missed that one and then counted a later relist as
   * new. Make private keeps `hostedAppId`, so bringing a private app back never counts.
   */
  const firstTime = !definition.hostedAppId && !definition.webUrl;
  const html = await assembleApp({
    appId: definition.id,
    source,
    appName: project.name,
    description: project.description,
  });
  const published = await publishToSaddle({
    definition,
    name: project.name,
    description: project.description,
    html,
    /*
     * Every publish rewrites the schema rows the server enforces, so every publish must say
     * what they are. Sent nothing, the server re-derives them from the document — no plan,
     * so no capacity, no one-per-person, every field text — and a label edit lifted the limit
     * the build had set. The same function the build uses.
     */
    schemas: schemasForPublish(source, definition.plan),
    ...(opts.putOnline ? { visibility: 'public' as const } : {}),
  });
  /*
   * Onto the record as it is AFTER the round trip, and only the fields publishing decides. The
   * fingerprint is of what was SENT: if the source moved while the page was going up, the page
   * has the older one, and the record says so.
   */
  const after = (await stored(projectId, ownerId)).definition;
  // Absent reads as live, as in publishToSaddle: only an explicit false is a page that is off.
  const live = published.webPublished !== false;
  /*
   * The address goes on the record only for a page that has one to open.
   *
   * The server answers a content update to a PRIVATE app with `webPublished: false` and its
   * siteUrl all the same, and a chat edit makes exactly that update — so writing siteUrl back
   * every time put a private app's address on its record, and the panel then showed the link,
   * "Copy link" and "Make private" for a page that serves nothing, and hid the Publish button
   * that was the only way to make it public again. So: a record that has an address keeps it (a
   * page taken offline is still at it); one without gets it only from a publish that put the
   * page up; and an address that went while the page was going up was removed by Make private,
   * which has the last word, whatever the publish that raced it came back with.
   */
  const keepsAddress = Boolean(after.webUrl) || (live && !definition.webUrl);
  await useProjects.getState().updateProject(
    projectId,
    {
      definition: markPublished(
        { ...after, ...(keepsAddress ? { webUrl: published.siteUrl } : {}), hostedAppId: published.appId },
        source,
      ),
    },
    ownerId,
  );
  // The event the ads optimise for: an app online for the first time, whichever button did it.
  if (firstTime && live) trackPixel('AppPublished');
  return published;
}

/**
 * Record that the page has no address any more, once the server has confirmed Make private.
 *
 * Onto the record as STORED, like everything here. Make private is a round trip too, and a chat
 * edit that saved during it is in the store and not in the screen's copy: writing the screen's
 * copy back laid the pre-edit source over the saved edit. `hostedAppId` stays — it is how the
 * app is published again at the same address.
 */
export async function recordPrivate(projectId: string, ownerId: string): Promise<void> {
  const next = { ...(await stored(projectId, ownerId)).definition };
  delete (next as { webUrl?: string }).webUrl;
  await useProjects.getState().updateProject(projectId, { definition: next }, ownerId);
}
